import { Component, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom, merge } from 'rxjs';
import { filter, map, switchMap, tap } from 'rxjs/operators';

import { AsyncPipe } from '@angular/common';
import { I18nService } from '@app/i18n';
import { environment } from '@env/environment';
import { Store } from '@ngrx/store';
import { AuthActions, ModalConfirmComponent, ToastComponent, UntilDestroy, untilDestroyed } from '@shared';
import { AuthenticationService } from '@shared/services';
import { BreadcrumbService } from '@shared/services/breadcrumb/breadcrumb.service';
import { CategoryActions, Logger, ModalService, ToastService, TopicActions, isDefined } from 'thkee-common';
import { ModalConfirmComponent as ModalConfirmComponent_1 } from './@shared/components/modal-confirm/modal-confirm.component';
import { ToastComponent as ToastComponent_1 } from './@shared/components/toast/toast.component';

const log = new Logger('App');

@UntilDestroy()
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [RouterOutlet, ModalConfirmComponent_1, ToastComponent_1, AsyncPipe],
})
export class AppComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private titleService = inject(Title);
  private translateService = inject(TranslateService);
  private i18nService = inject(I18nService);
  private store = inject(Store);
  private modalService = inject(ModalService);
  private toastService = inject(ToastService);
  private breadCrumb = inject(BreadcrumbService);
  private authService = inject(AuthenticationService);

  @ViewChild('modalConfirm')
  private modalConfirm!: ModalConfirmComponent;

  @ViewChild('toastComponent')
  private toastComponent!: ToastComponent;

  ngOnInit() {
    // Setup logger
    if (environment.production) {
      Logger.enableProductionMode();
    }

    // Setup translations
    this.i18nService.init(environment.defaultLanguage, environment.supportedLanguages);

    const onNavigationEnd = this.router.events.pipe(filter((event) => event instanceof NavigationEnd));

    // Change page title on navigation or language change, based on route data
    merge(this.translateService.onLangChange, onNavigationEnd)
      .pipe(
        map(() => {
          let route = this.activatedRoute;
          while (route.firstChild) {
            route = route.firstChild;
          }
          return route;
        }),
        filter((route) => route.outlet === 'primary'),
        switchMap((route) => route.data),
        untilDestroyed(this)
      )
      .subscribe((event) => {
        const title = event['title'];
        if (title) {
          this.titleService.setTitle(this.translateService.instant(title));
        }
      });

    // Init modal
    this.modalService.modal$
      .pipe(
        filter(isDefined),
        tap((data) => log.debug(data)),
        untilDestroyed(this)
      )
      .subscribe((data) => {
        this.modalConfirm.open();
      });

    this.toastService.toast$
      .pipe(
        filter(isDefined),
        tap((data) => log.debug(data)),
        untilDestroyed(this)
      )
      .subscribe((data) => {
        this.toastComponent.open();
      });

    firstValueFrom(this.authService.sessionExist()).then((isLogged) => {
      if (isLogged) {
        // Init ngrx store
        this.store.dispatch(CategoryActions.loadCategories());
        this.store.dispatch(TopicActions.loadTopics());
        this.store.dispatch(AuthActions.userLoad());
        return;
      } else {
      }
    });
  }

  ngOnDestroy() {
    this.i18nService.destroy();
  }

  get modal$() {
    return this.modalService.modal$;
  }

  get toast$() {
    return this.toastService.toast$;
  }
}
